import { clampTarget } from './rowTree';

export type TypedMoveFailReason = 'empty' | 'not_integer' | 'out_of_range' | 'invalid_target';

export type TypedMoveResult =
  | { ok: true; to: number | null }
  | { ok: false; reason: TypedMoveFailReason };

/**
 * Strict parse of a typed 1-based position. Does **not** snap to a nearby
 * legal landing — invalid / inside-another-group is a hard reject.
 */
export function resolveTypedMoveTarget(
  raw: unknown,
  from: number,
  movedCount: number,
  dataLen: number,
  valid?: readonly number[] | null
): TypedMoveResult {
  if (raw === '' || raw == null) return { ok: true, to: null };
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim().replace(',', '.'));
  if (!Number.isFinite(n) || !Number.isInteger(n)) return { ok: false, reason: 'not_integer' };
  if (!Number.isFinite(from) || from < 0 || from >= dataLen) {
    return { ok: false, reason: 'out_of_range' };
  }
  const count = Math.max(1, Math.floor(movedCount));
  const maxStart = Math.max(1, dataLen - count + 1);
  if (n < 1 || n > maxStart) return { ok: false, reason: 'out_of_range' };
  const to = n - 1;
  if (valid && valid.length > 0 && !valid.includes(to)) {
    return { ok: false, reason: 'invalid_target' };
  }
  if (to === from) return { ok: true, to: null };
  if (to < 0 || to + count > dataLen) return { ok: false, reason: 'out_of_range' };
  return { ok: true, to };
}

/**
 * Map a 1-based target position (what the user types) to the `onRowMoved`
 * final index. `null` = invalid / no-op. Snaps to the nearest legal sibling
 * landing when `valid` is supplied (context-menu / drag).
 */
export function moveToPositionTarget(
  from: number,
  desiredPos1: number,
  movedCount: number,
  dataLen: number,
  valid?: readonly number[] | null
): number | null {
  if (!Number.isFinite(from) || from < 0 || from >= dataLen) return null;
  if (!Number.isFinite(desiredPos1)) return null;
  const count = Math.max(1, Math.floor(movedCount));
  const maxStart = Math.max(1, dataLen - count + 1);
  const pos = Math.round(desiredPos1);
  if (pos < 1) return null;
  let to = Math.min(maxStart, pos) - 1;
  if (valid && valid.length > 0) {
    to = clampTarget([...valid], to, from);
  }
  if (to === from) return null;
  if (to < 0 || to + count > dataLen) return null;
  return to;
}
